import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Music, Trash2, Youtube, Send, EyeOff, Disc3, Music2, Flame, ListMusic, Mic2, ChevronUp } from "lucide-react";
import { api, formatError } from "../lib/api";
import { Screen, PageHeader, Empty } from "../components/ui-kit";

export default function RequestSong() {
  const [tab, setTab] = useState("request");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [mine, setMine] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [history, setHistory] = useState([]);
  const [billboard, setBillboard] = useState([]);
  const [climbing, setClimbing] = useState(new Set());
  const prevRankRef = useRef({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!billboard.length) return;
    const prev = prevRankRef.current;
    const hadPrev = Object.keys(prev).length > 0;
    const climbed = new Set();
    billboard.forEach((s, i) => {
      const p = prev[s.url_key];
      if (p && (i < p.rank || s.count > p.count)) climbed.add(s.url_key);
    });
    const next = {};
    billboard.forEach((s, i) => { next[s.url_key] = { rank: i, count: s.count }; });
    prevRankRef.current = next;
    if (hadPrev && climbed.size) {
      setClimbing(climbed);
      const t = setTimeout(() => setClimbing(new Set()), 3000);
      return () => clearTimeout(t);
    }
  }, [billboard]);

  const loadBillboard = () => api.get("/song-requests/billboard").then((r) => setBillboard(r.data)).catch(() => {});
  const load = () => {
    api.get("/song-requests/mine").then((r) => setMine(r.data)).catch(() => {});
    api.get("/song-requests/now-playing").then((r) => setNowPlaying(r.data)).catch(() => {});
    api.get("/song-requests/history").then((r) => setHistory(r.data)).catch(() => {});
    loadBillboard();
  };
  useEffect(() => { load(); const t = setInterval(() => { api.get("/song-requests/now-playing").then((r) => setNowPlaying(r.data)).catch(() => {}); api.get("/song-requests/history").then((r) => setHistory(r.data)).catch(() => {}); loadBillboard(); }, 20000); return () => clearInterval(t); }, []);

  const submit = async () => {
    if (!url.trim()) { toast.error("Paste a YouTube or Spotify song link"); return; }
    setBusy(true);
    try {
      const r = await api.post("/song-requests", { url: url.trim(), title: title.trim() });
      if (r.data.status === "already") toast.message(r.data.message || "You've already requested this song.");
      else toast.success("Song requested — thank you!");
      setUrl(""); setTitle(""); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/song-requests/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const TABS = [
    { key: "request", label: "Request a song", icon: Mic2 },
    { key: "billboard", label: "Billboard", icon: ListMusic },
  ];

  return (
    <Screen>
      <PageHeader overline="Dance floor" title="Request a Song" />

      {nowPlaying && (
        <div data-testid="now-playing-banner" className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-4 mb-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0"><Disc3 className="text-[#D4AF37]" size={20} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-[#D4AF37] font-semibold">Now spinning</p>
            <a href={nowPlaying.url} target="_blank" rel="noreferrer" data-testid="crowd-now-playing" className="text-sm font-medium truncate block">{nowPlaying.title || nowPlaying.url}</a>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-5" data-testid="request-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`request-tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold border transition-colors ${on ? "bg-[#D4AF37] text-black border-transparent" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>
              <Icon size={16} /> {t.label}{t.key === "billboard" && billboard.length > 0 ? ` (${billboard.length})` : ""}
            </button>
          );
        })}
      </div>

      {tab === "request" ? (
        <>
          <div className="rounded-2xl bg-gradient-to-br from-[#5b21b6]/25 to-[#0e7490]/20 border border-white/10 p-4 flex items-start gap-3" data-testid="request-anon-note">
            <EyeOff size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <p className="text-sm text-[#E8E4F0]">Your song request is <span className="font-semibold text-[#D4AF37]">100% anonymous</span> — the DJ only sees the song and how many people asked for it, never who.</p>
          </div>

          <div className="rounded-3xl bg-white/5 border border-white/10 p-4 mt-4 space-y-3">
            <div>
              <label className="text-xs text-[#A19DB0]">YouTube or Spotify link</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="song-url-input"
                placeholder="https://open.spotify.com/track/…  or  https://youtu.be/…"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 mt-1 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/30" />
            </div>
            <div>
              <label className="text-xs text-[#A19DB0]">Song name (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="song-title-input"
                placeholder="e.g. Vivir Mi Vida — Marc Anthony"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 mt-1 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/30" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="song-submit-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black py-3 text-sm font-semibold active:scale-[0.98] transition-transform disabled:opacity-50">
              <Send size={16} /> {busy ? "Sending…" : "Request this song"}
            </button>
          </div>

          {history.filter((h) => h.url_key !== nowPlaying?.url_key).length > 0 && (
            <div className="mt-6" data-testid="crowd-history">
              <h2 className="text-[11px] uppercase tracking-wide text-[#A19DB0] font-semibold mb-2">Just played</h2>
              <div className="space-y-1.5">
                {history.filter((h) => h.url_key !== nowPlaying?.url_key).map((h) => (
                  <a key={h.url_key} href={h.url} target="_blank" rel="noreferrer" data-testid={`crowd-history-${h.url_key}`} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2 opacity-80 hover:opacity-100 transition-opacity">
                    <Music2 size={14} className="text-[#A19DB0] shrink-0" />
                    <p className="text-xs truncate flex-1">{h.title || h.url}</p>
                    <span className="text-[10px] text-[#A19DB0] capitalize shrink-0">{h.platform}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <h2 className="font-display text-lg font-semibold mt-6 mb-3">Your requests</h2>
          {mine.length === 0 ? <Empty icon={Music} text="You haven't requested any songs yet." /> : (
            <div className="space-y-2" data-testid="my-song-list">
              {mine.map((s) => (
                <div key={s.id} data-testid={`my-song-${s.id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.platform === "youtube" ? "bg-[#FF0000]/15 text-[#FF6B6B]" : "bg-[#1DB954]/15 text-[#1DB954]"}`}>
                    {s.platform === "youtube" ? <Youtube size={17} /> : <Music size={17} />}
                  </span>
                  <a href={s.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0">
                    <p className="text-sm truncate">{s.title || s.url}</p>
                    <p className="text-[#A19DB0] text-xs capitalize flex items-center gap-2">{s.platform}{s.played && <span data-testid={`played-badge-${s.id}`} className="text-[#D4AF37] normal-case font-medium flex items-center gap-1"><Music2 size={11} /> Played</span>}</p>
                  </a>
                  <button onClick={() => remove(s.id)} data-testid={`remove-song-${s.id}`} className="w-8 h-8 rounded-full bg-[#E57373]/15 text-[#E57373] flex items-center justify-center hover:bg-[#E57373]/25 shrink-0"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div data-testid="billboard-view">
          <p className="text-[#A19DB0] text-sm mb-4">Every requested song right now — YouTube and Spotify together, ranked by how many people asked. All anonymous.</p>
          {billboard.length === 0 ? <Empty icon={ListMusic} text="No songs requested yet. Be the first!" /> : (
            <div className="space-y-2" data-testid="billboard-list">
              {billboard.map((s, i) => (
                <a key={s.url_key} href={s.url} target="_blank" rel="noreferrer" data-testid={`billboard-song-${i}`}
                  className={`flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3 hover:bg-white/10 transition-colors ${climbing.has(s.url_key) ? "billboard-ping" : ""}`}
                  style={i === 0 ? { borderColor: "#D4AF3766", background: "rgba(212,175,55,0.10)" } : undefined}>
                  <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sm font-bold shrink-0" style={i === 0 ? { color: "#D4AF37" } : undefined}>{i + 1}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${s.platform === "youtube" ? "bg-[#FF0000]/15 text-[#FF6B6B]" : "bg-[#1DB954]/15 text-[#1DB954]"}`}>
                    {s.platform === "youtube" ? <Youtube size={16} /> : <Music size={16} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate font-medium">{s.title || s.url}</p>
                    <p className="text-[#A19DB0] text-xs capitalize">{s.platform}</p>
                  </div>
                  {climbing.has(s.url_key) && <span data-testid={`billboard-climb-${i}`} className="flex items-center text-[#4ADE80] font-bold shrink-0 animate-pulse"><ChevronUp size={18} /></span>}
                  <span data-testid={`billboard-count-${i}`} className="flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full shrink-0 bg-[#FF7A45]/15 text-[#FF7A45]">
                    <Flame size={14} /> {s.count}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </Screen>
  );
}
